import { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router';
import Layout from 'client/components/suc/Layout/Layout';
import ErrorBoundary from 'client/utils/routes/ErrorBoundary/ErrorBoundary';
import LoadingPage from 'client/pages/LoadingPage/LoadingPage';
import { OrganisationModal } from 'client/components/suc/OrganisationModal/OrganisationModal';
import { NO_ORGANISATIONS_PAGE } from 'client/utils/routes/routeConstants';
import useAuth from 'client/utils/auth/useAuth';
import PropTypes from 'prop-types';
import { ProfileService } from 'client/services';
import { portalBaseUrl } from 'client/utils/constants/frontEndConfig';
import { useAccount, useMsal } from '@azure/msal-react';
import { logWithPrefix } from 'client/utils/logging';
const log = logWithPrefix('OrganisationGate');

const OrganisationGate = ({ children, setOrganisationId }) => {
    const { instance } = useMsal();
    const { signOutByChoice, signOutRedirect } = useAuth();
    const account = useAccount();
    const [hasChosenOrganisation, setHasChosenOrganisation] = useState(Boolean(account?.idTokenClaims?.organisationId));
    const [possibleOrganisations, setPossibleOrganisations] = useState(null);
    const [hasFinishedWaitingForMsal, setHasFinishedWaitingForMsal] = useState(false);
    const needsOrganisation = account && !hasChosenOrganisation;

    useEffect(() => {
        instance.handleRedirectPromise().then(() => {
            setHasFinishedWaitingForMsal(true);
        });
    }, []);

    useEffect(() => {
        if (needsOrganisation) {
            getOrganisationList();
        }
    }, [needsOrganisation]);

    useEffect(() => {
        const hasFetchedPossibleOrganisations = Array.isArray(possibleOrganisations);
        if (needsOrganisation && hasFetchedPossibleOrganisations && possibleOrganisations.length === 0) {
            log('No Possible Organisations', 'Assigned organisation needs checking').promise.finally(() => {
                signOutRedirect(`${portalBaseUrl}${NO_ORGANISATIONS_PAGE}`);
            });
        }
    }, [needsOrganisation, possibleOrganisations]);
    const handleSetOrganisationId = (organisationId) => {
        setOrganisationId(organisationId).then(() => setHasChosenOrganisation(true));
    };

    const userExplicitlyChoseOrganisation = (id) => {
        // Making the distinction between 'system automatically set organisation because there's only one'
        // and 'user picked this organisation from a list'
        log('User Chose Organisation ID', id);
        handleSetOrganisationId(id);
    };

    const getOrganisationList = () => {
        ProfileService.getOrganisations().then(response => {
            const myOrganisations = response.organisations;
            setPossibleOrganisations(myOrganisations);
            if (myOrganisations.length === 1) {
                log('System Set Organisation ID', myOrganisations[0].organisationId);
                handleSetOrganisationId(myOrganisations[0].organisationId);
            }
        }).catch((err) => {
            log('Error Fetching Organisations', err);
            setPossibleOrganisations([]);
        });
    };

    if (hasFinishedWaitingForMsal && !needsOrganisation) {
        // Don't do anything - pass through to the rest of the app
        // In App.js, the children passed to us contains the real router of the app
        return <>{children}</>;
    }

    /*
    * In order to prevent loaders from routes.js kicking off without an organisation attached,
    * we don't render the standard router at all if the user is signed in without an organisation.
    * Instead, we display an OrganisationModal.
    * We have to wrap OrganisationModal in it's very own special little router because we
    * want to preserve URLs, so we set up a wildcard router so that e.g /bookmarks works
    * Also to make the modal feel part of the app we show the header and footer behind it,
    * and components inside Layout contain NavLinks, which have to be mounted inside a router.
    * Once they have made a choice, we render the app's real router (children) instead of this one.
    * When react router v6 gets `beforeLoader`, there might be a better way to do this
    * See discussion here for further reading: https://github.com/remix-run/react-router/discussions/9564
    * */
    const catchAllRouter = [{
        id: 'OrganisationModal',
        path: '*',
        hydrateFallbackElement: <></>,
        element: (
            <Layout>
                {(hasFinishedWaitingForMsal && possibleOrganisations) &&
                    <OrganisationModal
                        setOrganisationId={userExplicitlyChoseOrganisation}
                        handleCancel={signOutByChoice}
                        organisations={possibleOrganisations || []}
                        cancelButtonI18nKey='general.signOut'
                        open={!hasChosenOrganisation && possibleOrganisations?.length > 1}
                    />}
                <LoadingPage />
            </Layout>
        ),
        errorElement: (
            <Layout>
                <ErrorBoundary/>
            </Layout>
        )
    }];

    const routes = createBrowserRouter(catchAllRouter);
    return <RouterProvider router={routes} />;
};

OrganisationGate.propTypes = {
    children: PropTypes.node.isRequired,
    setOrganisationId: PropTypes.func.isRequired
};

export default OrganisationGate;
